import React, {Component} from 'react';
import enhanceWithClickOutside from 'react-click-outside';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/uniqueId';

import PasswordChecklist from '../PasswordChecklist';
import PasswordVisibility from '../PasswordVisibility';

import {Title, Error} from 'styles/styled-components/field-styled-components';

import {
  Field,
  Input,
  TextArea,
  InlineInput,
  InlineInputWrapper,
  InputWithShadowText,
  ShadowText,
  VisibilityIconWrapper,
  InputWrapper,
  CharactersInfo
} from './styles';

@enhanceWithClickOutside
class FieldComponent extends Component {
  static defaultProps = {
    autoComplete: 'on',
    autofocus: false,
    customError: '',
    inline: false,
    inputType: 'input',
    min: 0,
    readOnly: false,
    styles: {},
    type: 'text'
  };

  static propTypes = {
    autoComplete: PropTypes.string,
    autofocus: PropTypes.bool,
    className: PropTypes.string,
    customError: PropTypes.string,
    dataCy: PropTypes.string,
    defaultValue: PropTypes.string,
    disabled: PropTypes.bool,
    field: PropTypes.string,
    forcedError: PropTypes.string,
    form: PropTypes.object,
    inline: PropTypes.bool,
    inputComponent: PropTypes.element,
    inputType: PropTypes.string,
    icon: PropTypes.element,
    max: PropTypes.number,
    maxLength: PropTypes.number,
    min: PropTypes.number,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    placeholder: PropTypes.string,
    readOnly: PropTypes.bool,
    shadowText: PropTypes.object,
    size: PropTypes.string,
    styles: PropTypes.object,
    title: PropTypes.string,
    titleDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    type: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.string,
    error: PropTypes.string,
    isInputFocused: PropTypes.bool,
    hideArrows: PropTypes.bool,
    maxFeedbackLength: PropTypes.number
  };

  constructor(props) {
    super(props);
    this.state = {editMode: false, showPassword: false};
  }

  togglePassword = () =>
    this.setState({
      showPassword: !this.state.showPassword
    });

  toggleEditMode = (value = !this.state.editMode) => {
    const {readOnly = false, shadowText, field} = this.props;
    if (readOnly) return;

    this.setState({
      editMode: value
    });

    if (shadowText && value) {
      const input = document.getElementById(`${field}-inline-input`);

      input && input.focus();
    }
  };

  inputId = this.props.inputId || uniqueId('field-');
  handleInputKeydown = event => {
    const {onSubmit, onCancel} = this.props;

    if (!this.state.editMode) return;

    if (onSubmit && event.keyCode === 13) {
      onSubmit();
      this.toggleEditMode();
    }

    if (onCancel && event.keyCode === 27) {
      onCancel();
      this.toggleEditMode();
    }
  };

  handleClickOutside = () => {
    const {shadowText, inline = false, onSubmit} = this.props;
    if (this.state.editMode && inline && onSubmit) {
      onSubmit();
      this.toggleEditMode();
    } else if (this.state.editMode && shadowText) {
      this.toggleEditMode();
    }
  };

  render() {
    const {
      autoComplete,
      autofocus,
      className,
      customError,
      dataCy,
      defaultValue,
      disabled,
      field,
      forcedError,
      form,
      inline,
      inputComponent,
      inputType,
      max,
      maxLength,
      min,
      readOnly,
      placeholder,
      size,
      shadowText,
      styles,
      title,
      titleDescription,
      icon,
      width,
      display,
      customErrorStyle,
      dataTestId,
      hideError,
      onlyIntegers,
      withPasswordValidation,
      withPasswordToggle,
      name,
      value,
      onChange,
      onFocus,
      onBlur,
      error,
      isInputFocused,
      maxFeedbackLength
    } = this.props;
    let {type} = this.props;

    const isInputWithShadowText = shadowText && (shadowText.beforeInput || shadowText.afterInput);
    const isUnderlinedTextbox = !isInputWithShadowText && !inputComponent && inline;
    const isTextarea = !isInputWithShadowText && !isUnderlinedTextbox && !inputComponent && inputType === 'textarea';
    const isInput = !isInputWithShadowText && !isUnderlinedTextbox && !inputComponent && inputType === 'input';
    const inputError = error || forcedError;

    if (this.state.showPassword) {
      type = 'input';
    }

    return (
      <Field
        size={size}
        className={className}
        inline={inline}
        editMode={this.state.editMode}
        data-cy={dataCy}
        {...(inline && {onClick: this.toggleEditMode})}
        display={display}
        withPasswordValidation={withPasswordValidation}
        hideArrows={this.props.hideArrows}
      >
        {title && (
          <Title htmlFor={this.inputId} style={styles.Title}>
            {title}
            {titleDescription && icon}
          </Title>
        )}

        {inputComponent}

        {isTextarea && (
          <InputWrapper>
            <TextArea
              id={this.inputId}
              readOnly={this.readOnly}
              styles={styles.Input}
              placeholder={placeholder}
              autoComplete={autoComplete}
              autoFocus={autofocus}
              {...(onChange && {onChange})}
              defaultValue={defaultValue}
              name={name}
              value={value}
            />
            {maxFeedbackLength && (
              <CharactersInfo>
                {value?.length ?? 0}/{maxFeedbackLength}
              </CharactersInfo>
            )}
          </InputWrapper>
        )}

        {isInput && (
          <InputWrapper>
            <Input
              id={this.inputId}
              readOnly={readOnly}
              styles={styles.Input}
              type={type}
              min={min}
              max={max}
              maxLength={maxLength}
              disabled={disabled}
              placeholder={placeholder}
              autoComplete={autoComplete}
              data-cy={dataCy}
              data-testid={dataTestId}
              autoFocus={autofocus}
              {...(onChange && {onChange})}
              defaultValue={defaultValue}
              width={width}
              onKeyPress={e => {
                const isMatch =
                  e.key === ',' || e.key === '.' || e.key.toLowerCase() === 'e' || e.key === '+' || e.key === '-';
                if (onlyIntegers && isMatch) {
                  e.preventDefault();
                }
              }}
              withPasswordValidation={withPasswordValidation}
              name={name}
              value={value}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              isInputFocused={isInputFocused}
            />
            {withPasswordToggle && (
              <VisibilityIconWrapper>
                <PasswordVisibility
                  togglePassword={this.togglePassword}
                  showPassword={this.state.showPassword}
                  password={form.$(field).value}
                />
              </VisibilityIconWrapper>
            )}
            {withPasswordValidation && (
              <PasswordChecklist password={form.$(field).value} isInputFocused={isInputFocused} />
            )}
            {maxFeedbackLength && (
              <CharactersInfo>
                {value?.length ?? 0}/{maxFeedbackLength}
              </CharactersInfo>
            )}
          </InputWrapper>
        )}

        {isUnderlinedTextbox && (
          <InlineInputWrapper>
            <InlineInput
              id={this.inputId}
              onKeyDown={this.handleInputKeydown}
              readOnly={readOnly || !this.state.editMode}
              style={styles.Input}
              {...((autofocus || this.state.editMode) && {autofocus: true})}
              name={name}
              value={value}
              onChange={onChange}
            />
            {!readOnly && icon}
          </InlineInputWrapper>
        )}

        {isInputWithShadowText && (
          <InputWithShadowText onClick={() => this.toggleEditMode(true)} focus={this.state.editMode}>
            {shadowText.beforeInput && <ShadowText>{shadowText.beforeInput}</ShadowText>}
            <InlineInput
              id={this.inputId}
              readOnly={readOnly}
              styles={styles.Input}
              disabled={disabled}
              placeholder={placeholder}
              autoComplete={autoComplete}
              autoFocus={autofocus}
              {...(onChange && {onChange})}
              defaultValue={defaultValue}
              name={name}
              value={value}
            />
            {shadowText.afterInput && <ShadowText style={{marginLeft: '-3px'}}>{shadowText.afterInput}</ShadowText>}
          </InputWithShadowText>
        )}

        {!disabled && !hideError && !withPasswordValidation && (
          <Error
            inline={inline}
            visible={customError || (!!inputError && inputError !== 'validating...')}
            {...customErrorStyle}
          >
            {inputError || error}
          </Error>
        )}
      </Field>
    );
  }
}

export default FieldComponent;
